import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

function isOpaqueSupabaseKey(value: string): boolean {
  return value.startsWith("sb_publishable_") || value.startsWith("sb_secret_");
}

function createServerSupabaseFetch(supabaseKey: string): typeof fetch {
  return (input, init) => {
    const headers = new Headers(
      typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined,
    );
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }
    if (isOpaqueSupabaseKey(supabaseKey) && headers.get("Authorization") === `Bearer ${supabaseKey}`) {
      headers.delete("Authorization");
    }
    headers.set("apikey", supabaseKey);
    return fetch(input, { ...init, headers });
  };
}

function serverPublic() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    global: { fetch: createServerSupabaseFetch(key) },
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

export type PublicTestimonial = {
  id: string;
  author: string;
  role: string;
  content: string;
  rating: number;
  source: string;
};

export type PublicTeamMember = {
  id: string;
  name: string;
  role: string;
  bio: string;
  photo_url: string | null;
  linkedin_url: string | null;
};

export type PublicPost = {
  id: string;
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  category: string;
  cover: string;
  body: string;
  author: string;
  reading_time: string;
  published_at: string | null;
  cover_image: string | null;
  youtube_urls: string[];
  article_type?: string | null;
  product_name?: string | null;
  product_image?: string | null;
  product_image_alt?: string | null;
  product_url?: string | null;
  product_price?: string | null;
  product_rating?: number | null;
  product_reviews_count?: number | null;
  product_features?: string[] | null;
  product_cta_label?: string | null;
  product_marketplace?: string | null;
  product_cta_bg_color?: string | null;
  product_cta_text_color?: string | null;
  comparison_products?: Array<Record<string, any>> | null;
};

const PUBLIC_POST_COLUMNS = "id, slug, title, description, excerpt, category, cover, body, author, reading_time, published_at, cover_image, youtube_urls, article_type, product_name, product_image, product_image_alt, product_url, product_price, product_rating, product_reviews_count, product_features, product_cta_label, product_marketplace, product_cta_bg_color, product_cta_text_color, comparison_products, business_data";

// Colunas leves usadas em listagens (blog index, home, busca).
// Não inclui `body`, `product_features` ou `comparison_products` — payloads
// grandes que só são necessários na página de detalhe do artigo.
const PUBLIC_POST_LIST_COLUMNS = "id, slug, title, description, excerpt, category, cover, author, reading_time, published_at, cover_image, youtube_urls, article_type, product_name, product_image, product_url, product_price, product_rating, product_reviews_count, product_cta_label, product_marketplace, product_cta_bg_color, product_cta_text_color";

// Cache in-process para regras de links internos (mudam raramente).
// Evita re-consultar a tabela a cada renderização de artigo/local.
let _internalLinksCache: { data: Array<{ anchor: string; url: string; priority: number }>; expiresAt: number } | null = null;
const INTERNAL_LINKS_TTL_MS = 30 * 60 * 1000;


async function getInternalLinksCached(sb: ReturnType<typeof serverPublic>) {
  const now = Date.now();
  if (_internalLinksCache && _internalLinksCache.expiresAt > now) {
    return _internalLinksCache.data;
  }
  const { data } = await sb
    .from("internal_links")
    .select("anchor, url, priority")
    .eq("active", true)
    .order("priority", { ascending: false });
  const rows = (data ?? []) as Array<{ anchor: string; url: string; priority: number }>;
  _internalLinksCache = { data: rows, expiresAt: now + INTERNAL_LINKS_TTL_MS };
  return rows;
}

// ───────── Pillar pages (public) ─────────

export type PillarItem = {
  position?: number;
  name?: string;
  brand?: string;
  image?: string;
  image_alt?: string;
  rating?: number;
  short_desc?: string;
  best_for?: string;
  price_from?: string;
  pros?: string[];
  cons?: string[];
  features?: string[];
  affiliate_url?: string;
  affiliate_label?: string;
  marketplace?: string;
  review_url?: string;
  paragraph?: string;
};

export type PublicPillarPage = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  intro: string | null;
  eyebrow: string | null;
  meta_title: string | null;
  meta_description: string | null;
  hero_image: string | null;
  conclusion: string | null;
  items: PillarItem[];
};

export const getPillarPage = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: row } = await sb
      .from("pillar_pages" as any)
      .select("id, slug, title, subtitle, intro, eyebrow, meta_title, meta_description, hero_image, conclusion, items")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (!row) return null;
    return row as unknown as PublicPillarPage;
  });

export const listTestimonials = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("testimonials")
    .select("id, author, role, content, rating, source")
    .eq("featured", true)
    .order("order_index", { ascending: true });
  if (error) return [] as PublicTestimonial[];
  return (data ?? []) as PublicTestimonial[];
});

export const listTeam = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("team_members")
    .select("id, name, role, bio, photo_url, linkedin_url")
    .eq("active", true)
    .order("order_index", { ascending: true });
  if (error) return [] as PublicTeamMember[];
  return (data ?? []) as PublicTeamMember[];
});

// Lista com limite explícito. Usada pela busca global e por listagens leves;
// o blog agora usa `listPublishedPostsPaged` para infinite scroll.
export const listPublishedPosts = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("posts")
    .select(PUBLIC_POST_LIST_COLUMNS)
    .eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(500);
  if (error) return [] as PublicPost[];
  return (data ?? []) as unknown as PublicPost[];
});

// Versão paginada para infinite scroll do blog.
export const listPublishedPostsPaged = createServerFn({ method: "GET" })
  .inputValidator((d: { limit?: number; offset?: number }) => d ?? {})
  .handler(async ({ data }) => {
    const limit = Math.min(50, Math.max(1, data?.limit ?? 12));
    const offset = Math.max(0, data?.offset ?? 0);
    const sb = serverPublic();
    const { data: rows, count, error } = await sb
      .from("posts")
      .select(PUBLIC_POST_LIST_COLUMNS, { count: "exact" })
      .eq("published", true)
      .order("published_at", { ascending: false, nullsFirst: false })
      .range(offset, offset + limit - 1);
    if (error) return { rows: [] as PublicPost[], total: 0 };
    return { rows: (rows ?? []) as unknown as PublicPost[], total: count ?? 0 };
  });

export const getPublishedPost = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: post } = await sb
      .from("posts")
      .select(PUBLIC_POST_COLUMNS)
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    return (post ?? null) as PublicPost | null;
  });

// Returns the post plus the active internal-link rules and pre-rendered HTML
// (markdown body converted to HTML and internal links auto-applied).
export const getRenderedPost = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const [postRes, links] = await Promise.all([
      sb
        .from("posts")
        .select(`${PUBLIC_POST_COLUMNS}, keywords`)
        .eq("slug", data.slug)
        .eq("published", true)
        .maybeSingle(),
      getInternalLinksCached(sb),
    ]);
    const post = postRes.data;
    if (!post) return null;
    const { marked } = await import("marked");
    const { applyInternalLinks, transformFaqAccordions, addHeadingIds } = await import("./internal-links");
    const { sanitizePostHtml, ensureImageAlts } = await import("./sanitize-html.server");
    const rawHtml = await marked.parse(post.body || "");
    const safeHtml = ensureImageAlts(sanitizePostHtml(rawHtml as string), post.title, (post as any).keywords ?? []);
    const linked = applyInternalLinks(safeHtml, links as any);
    const html = addHeadingIds(transformFaqAccordions(linked));
    return { ...post, html } as PublicPost & { html: string };
  });

// ───────── Videos ─────────

export type PublicVideo = {
  id: string;
  title: string;
  url: string;
  description: string;
  thumbnail_url: string | null;
};

export const listPublishedVideos = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("blog_videos" as any)
    .select("id, title, url, description, thumbnail_url")
    .eq("published", true)
    .order("order_index", { ascending: true })
    .limit(24);
  if (error) return [] as PublicVideo[];
  return (data ?? []) as unknown as PublicVideo[];
});

// ───────── Service Locations ─────────

export type PublicServiceLocation = {
  id: string;
  slug: string;
  service_slug: string;
  service_label: string;
  city: string;
  neighborhood: string | null;
  title: string;
  description: string;
  body: string;
  cover_image: string | null;
  published_at: string | null;
};

export const listPublishedLocations = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("service_locations" as any)
    .select("id, slug, service_slug, service_label, city, neighborhood, title, description, body, cover_image, published_at")
    .eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(100);
  if (error) return [] as PublicServiceLocation[];
  return (data ?? []) as unknown as PublicServiceLocation[];
});

// ───────── Related posts ─────────

export type RelatedPost = {
  slug: string; title: string; category: string;
  cover: string; cover_image: string | null;
  excerpt: string; reading_time: string;
};

export const listRelatedPosts = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string; category: string; limit?: number }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const limit = data.limit ?? 4;
    // Prefer same-category posts
    const sameCat = await sb
      .from("posts")
      .select("slug, title, category, cover, cover_image, excerpt, description, reading_time")
      .eq("published", true)
      .eq("category", data.category)
      .neq("slug", data.slug)
      .order("published_at", { ascending: false, nullsFirst: false })
      .limit(limit);
    const rows = sameCat.data ?? [];
    if (rows.length < limit) {
      const fill = await sb
        .from("posts")
        .select("slug, title, category, cover, cover_image, excerpt, description, reading_time")
        .eq("published", true)
        .neq("slug", data.slug)
        .neq("category", data.category)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(limit - rows.length);
      for (const r of fill.data ?? []) rows.push(r as any);
    }
    return rows.map((p: any) => ({
      slug: p.slug, title: p.title, category: p.category,
      cover: p.cover, cover_image: p.cover_image,
      excerpt: p.excerpt || p.description || "",
      reading_time: p.reading_time,
    })) as RelatedPost[];
  });

// ───────── Services (public) ─────────

export type PublicService = {
  id: string; slug: string; label: string;
  description: string; icon: string; route_path: string;
  order_index: number;
};

export const listActiveServices = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("services" as any)
    .select("id, slug, label, description, icon, route_path, order_index")
    .eq("active", true)
    .order("order_index", { ascending: true });
  if (error) return [] as PublicService[];
  return (data ?? []) as unknown as PublicService[];
});

export const getRenderedLocation = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const [locRes, links] = await Promise.all([
      sb
        .from("service_locations" as any)
        .select("id, slug, service_slug, service_label, city, neighborhood, title, description, body, cover_image, published_at")
        .eq("slug", data.slug)
        .eq("published", true)
        .maybeSingle(),
      getInternalLinksCached(sb),
    ]);
    const loc = locRes.data as unknown as (PublicServiceLocation & { keywords?: string[] }) | null;
    if (!loc) return null;
    const { marked } = await import("marked");
    const { applyInternalLinks, transformFaqAccordions, addHeadingIds } = await import("./internal-links");
    const { sanitizePostHtml, ensureImageAlts } = await import("./sanitize-html.server");
    const rawHtml = await marked.parse(loc.body || "");
    const locKeywords = [
      ...(Array.isArray(loc.keywords) ? loc.keywords : []),
      loc.neighborhood ? `${loc.service_label} em ${loc.neighborhood}` : "",
      loc.city ? `${loc.service_label} em ${loc.city}` : "",
    ].filter(Boolean) as string[];
    const safeHtml = ensureImageAlts(sanitizePostHtml(rawHtml as string), loc.title, locKeywords);
    const linked = applyInternalLinks(safeHtml, links as any);
    const html = addHeadingIds(transformFaqAccordions(linked));
    return { ...loc, html } as PublicServiceLocation & { html: string };
  });
// ───────── Product models (public) ─────────

export type PublicProductModel = {
  id: string;
  slug: string;
  name: string;
  brand: string | null;
  short_desc: string | null;
  long_intro: string[];
  pros: string[];
  cons: string[];
  best_for: string[];
  not_ideal_for: string[];
  specs: { label: string; value: string }[];
  faq: { q: string; a: string }[];
  highlights: string[];
  image_url: string | null;
  image_alt: string | null;
  price_from_brl: number | null;
  rating: number | null;
  reviews_count: number | null;
  affiliate_amazon: string | null;
  affiliate_mercadolivre: string | null;
  affiliate_magalu: string | null;
  affiliate_primary_url: string | null;
  affiliate_marketplace: string | null;
  affiliate_cta_label: string | null;
  ranking_position: number | null;
  meta_title: string | null;
  meta_description: string | null;
};

const PUBLIC_MODEL_COLS =
  "id, slug, name, brand, short_desc, long_intro, pros, cons, best_for, not_ideal_for, specs, faq, highlights, image_url, image_alt, price_from_brl, rating, reviews_count, affiliate_amazon, affiliate_mercadolivre, affiliate_magalu, affiliate_primary_url, affiliate_marketplace, affiliate_cta_label, ranking_position, meta_title, meta_description";

function normalizeModelRow(row: any): PublicProductModel {
  const arr = (v: any) => (Array.isArray(v) ? v : []);
  return {
    ...row,
    long_intro: arr(row.long_intro),
    pros: arr(row.pros),
    cons: arr(row.cons),
    best_for: arr(row.best_for),
    not_ideal_for: arr(row.not_ideal_for),
    specs: arr(row.specs),
    faq: arr(row.faq),
    highlights: arr(row.highlights),
  } as PublicProductModel;
}

export const getProductModel = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const sb = serverPublic();
    const { data: row } = await sb
      .from("product_models" as any)
      .select(PUBLIC_MODEL_COLS)
      .eq("slug", data.slug)
      .eq("active", true)
      .maybeSingle();
    if (!row) return null;
    return normalizeModelRow(row);
  });

export const listTopProductModels = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data } = await sb
    .from("product_models" as any)
    .select(PUBLIC_MODEL_COLS)
    .eq("active", true)
    .not("ranking_position", "is", null)
    .order("ranking_position", { ascending: true })
    .limit(12);
  return ((data ?? []) as any[]).map(normalizeModelRow);
});

export const listPublicProductModels = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data } = await sb
    .from("product_models" as any)
    .select(PUBLIC_MODEL_COLS)
    .eq("active", true)
    .order("ranking_position", { ascending: true, nullsFirst: false })
    .order("updated_at", { ascending: false })
    .limit(50);
  return ((data ?? []) as any[]).map(normalizeModelRow);
});
